'use client'

import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { formatPrice } from '@/lib/utils'

interface EditDishPriceModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (newUnitPrice: number) => void
  productName: string
  currentUnitPrice: number
  catalogBasePrice: number
}

export function EditDishPriceModal({
  isOpen,
  onClose,
  onSave,
  productName,
  currentUnitPrice,
  catalogBasePrice,
}: EditDishPriceModalProps) {
  const minPrice = Math.max(0, catalogBasePrice || 0)
  const [priceInput, setPriceInput] = useState<string>(String(currentUnitPrice))
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setPriceInput(String(currentUnitPrice))
      setErrorMsg(null)
    }
  }, [isOpen, currentUnitPrice])

  const handleInputChange = (val: string) => {
    // Only digits
    const cleaned = val.replace(/\D/g, '')
    setPriceInput(cleaned)

    const num = Number(cleaned)
    if (!cleaned || isNaN(num)) {
      setErrorMsg('Ingresa un precio válido')
    } else if (num < minPrice) {
      setErrorMsg(`El precio no puede ser inferior al precio base de ${formatPrice(minPrice)}`)
    } else {
      setErrorMsg(null)
    }
  }

  const handleQuickAdd = (delta: number) => {
    const current = Number(priceInput) || minPrice
    const next = Math.max(minPrice, current + delta)
    setPriceInput(String(next))
    setErrorMsg(null)
  }

  const handleReset = () => {
    setPriceInput(String(minPrice))
    setErrorMsg(null)
  }

  const parsedVal = Number(priceInput)
  const isValid = priceInput !== '' && !isNaN(parsedVal) && parsedVal >= minPrice

  const handleConfirm = () => {
    if (!isValid) return
    onSave(parsedVal)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-[#18181b] border-white/10 text-[#f0f0f0]">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#f0f0f0]">
            Modificar precio de plato
          </DialogTitle>
          <DialogDescription className="text-xs text-[#9a9aa8]">
            Ajuste de precio unitario específico (Modo Administrador)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-[#27272a] p-3 rounded-xl border border-white/10 flex flex-col gap-1">
            <span className="text-xs text-[#9a9aa8]">Producto:</span>
            <span className="font-semibold text-sm text-[#f0f0f0]">{productName}</span>
            <div className="flex items-center gap-2 mt-1 text-xs">
              <span className="text-[#9a9aa8]">Precio base en catálogo:</span>
              <span className="font-mono text-[#00e5a0] font-medium">
                {formatPrice(minPrice)}
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#d4d4d8]">
              Nuevo precio unitario (CLP)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#9a9aa8] font-mono">$</span>
              <Input
                type="text"
                inputMode="numeric"
                value={priceInput}
                onChange={(e) => handleInputChange(e.target.value)}
                className="pl-7 bg-[#27272a] border-white/10 text-[#f0f0f0] font-mono text-base focus:border-[#00e5a0] focus:ring-[#00e5a0]"
                placeholder={String(minPrice)}
              />
            </div>
            {errorMsg ? (
              <p className="text-xs text-red-400 font-medium mt-1">{errorMsg}</p>
            ) : (
              <p className="text-[11px] text-[#71717a] mt-1">
                * Solo se permiten precios positivos no inferiores a {formatPrice(minPrice)}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <span className="text-[11px] font-medium text-[#9a9aa8]">Ajustes rápidos:</span>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="text-xs bg-[#27272a] border-white/10 text-[#d4d4d8] hover:bg-[#3f3f46] hover:text-white"
              >
                Base ({formatPrice(minPrice)})
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickAdd(500)}
                className="text-xs bg-[#27272a] border-white/10 text-[#d4d4d8] hover:bg-[#3f3f46] hover:text-white"
              >
                + $500
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickAdd(1000)}
                className="text-xs bg-[#27272a] border-white/10 text-[#d4d4d8] hover:bg-[#3f3f46] hover:text-white"
              >
                + $1.000
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickAdd(2000)}
                className="text-xs bg-[#27272a] border-white/10 text-[#d4d4d8] hover:bg-[#3f3f46] hover:text-white"
              >
                + $2.000
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickAdd(5000)}
                className="text-xs bg-[#27272a] border-white/10 text-[#d4d4d8] hover:bg-[#3f3f46] hover:text-white"
              >
                + $5.000
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-xs text-[#9a9aa8] hover:text-white hover:bg-white/10"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!isValid}
            onClick={handleConfirm}
            className="bg-[#00e5a0] text-black hover:bg-[#00e5a0]/90 text-xs font-semibold"
          >
            Guardar precio {isValid ? `(${formatPrice(parsedVal)})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
